import { useEffect, useRef, useState } from "react";

import { Box, Button, Group, Menu, Stack } from "@mantine/core";
import { DatePicker, type DatesRangeValue } from "@mantine/dates";

import { IconCalendarMinus, IconChevronDown } from "@tabler/icons-react";
import { format } from "date-fns";

import {
  PRACTICE_LEDGER_DATE_RANGE_PRESET,
  PRACTICE_LEDGER_DATE_RANGE_PRESET_LABEL,
  type PracticeLedgerDateRange,
  type PracticeLedgerNamedDateRangePreset,
} from "@/billing/types/practice-ledger";
import {
  formatPracticeLedgerDateRangeTriggerLabel,
  getPracticeLedgerCustomDateRange,
  getPracticeLedgerDateRange,
} from "@/billing/utils/practice-ledger-date-range";
import { TextWrapper } from "@/components/TextWrapper";
import { parseMantineDateInputValue } from "@/utils/mantine-date-input";

import { PRACTICE_LEDGER_NAMED_PRESET_TEST_IDS } from "./constants/practice-ledger";
import type { PracticeLedgerDateRangeControlProps } from "./types/ledger-ui";

const NAMED_PRESETS: PracticeLedgerNamedDateRangePreset[] = [
  PRACTICE_LEDGER_DATE_RANGE_PRESET.CurrentMonth,
  PRACTICE_LEDGER_DATE_RANGE_PRESET.Last30Days,
  PRACTICE_LEDGER_DATE_RANGE_PRESET.Last90Days,
];

type DateRangeMenuView = "menu" | "calendar";

function toDatePickerRangeValue(dateRange: PracticeLedgerDateRange): DatesRangeValue {
  return [format(dateRange.startDate, "yyyy-MM-dd"), format(dateRange.endDate, "yyyy-MM-dd")];
}

/**
 * Builds a Show: range from a Mantine range value. Returns undefined when either date is missing.
 */
function resolveDateRangeFromPickerValue(
  value: DatesRangeValue,
): PracticeLedgerDateRange | undefined {
  const [rawStart, rawEnd] = value;
  if (!rawStart || !rawEnd) return undefined;
  return getPracticeLedgerCustomDateRange(
    parseMantineDateInputValue(rawStart),
    parseMantineDateInputValue(rawEnd),
  );
}

/**
 * Ledger Show: control. Named presets are Current Month, Last 30 days, and Last 90 days; Custom
 * opens a range DatePicker that only commits on Apply.
 */
export default function LedgerDateRangeControl({
  dateRange,
  preset,
  onSelectionChange,
}: PracticeLedgerDateRangeControlProps) {
  const dateRangeStartTimestamp = dateRange.startDate.getTime();
  const dateRangeEndTimestamp = dateRange.endDate.getTime();
  const [menuOpened, setMenuOpened] = useState(false);
  const [menuView, setMenuView] = useState<DateRangeMenuView>("menu");
  const [draftCustomRange, setDraftCustomRange] = useState<DatesRangeValue>(() =>
    toDatePickerRangeValue(dateRange),
  );
  const menuCloseReasonRef = useRef<"commit" | "dismiss" | null>(null);

  useEffect(() => {
    setDraftCustomRange(
      toDatePickerRangeValue({
        startDate: new Date(dateRangeStartTimestamp),
        endDate: new Date(dateRangeEndTimestamp),
      }),
    );
  }, [dateRangeEndTimestamp, dateRangeStartTimestamp]);

  const triggerLabel = formatPracticeLedgerDateRangeTriggerLabel(preset, dateRange);
  const canApplyCustomRange = Boolean(draftCustomRange[0] && draftCustomRange[1]);

  const resetDraftAndMenuView = () => {
    setDraftCustomRange(toDatePickerRangeValue(dateRange));
    setMenuView("menu");
  };

  const handleMenuOpenChange = (opened: boolean) => {
    if (opened) {
      setMenuOpened(true);
      return;
    }
    const closeReason = menuCloseReasonRef.current;
    menuCloseReasonRef.current = null;
    setMenuOpened(false);
    if (closeReason === "commit") {
      setMenuView("menu");
      return;
    }
    resetDraftAndMenuView();
  };

  const handleSelectNamedPreset = (namedPreset: PracticeLedgerNamedDateRangePreset) => {
    const nextRange = getPracticeLedgerDateRange(namedPreset);
    menuCloseReasonRef.current = "commit";
    setDraftCustomRange(toDatePickerRangeValue(nextRange));
    setMenuView("menu");
    setMenuOpened(false);
    onSelectionChange({ preset: namedPreset });
  };

  const handleSelectCustomRange = () => {
    setDraftCustomRange(toDatePickerRangeValue(dateRange));
    setMenuView("calendar");
  };

  const handleCancelCustomRange = () => {
    menuCloseReasonRef.current = "dismiss";
    resetDraftAndMenuView();
    setMenuOpened(false);
  };

  const handleApplyCustomRange = () => {
    const resolvedRange = resolveDateRangeFromPickerValue(draftCustomRange);
    if (!resolvedRange) return;
    menuCloseReasonRef.current = "commit";
    setDraftCustomRange(toDatePickerRangeValue(resolvedRange));
    setMenuView("menu");
    setMenuOpened(false);
    onSelectionChange({
      preset: PRACTICE_LEDGER_DATE_RANGE_PRESET.Custom,
      dateRange: resolvedRange,
    });
  };

  return (
    <Group gap="xs" wrap="wrap">
      <TextWrapper size="sm" lh="20px" c="gray.7" variant="bold">
        Show:
      </TextWrapper>
      <Menu
        shadow="md"
        radius="md"
        withinPortal
        opened={menuOpened}
        position="bottom-end"
        closeOnItemClick={false}
        onChange={handleMenuOpenChange}
        transitionProps={{ duration: 0 }}
        width={menuView === "menu" ? 280 : "auto"}
      >
        <Menu.Target>
          <Button
            radius="sm"
            variant="default"
            data-testid="practice-ledger-date-range-preset"
            aria-label="Show ledger activity for date range"
            rightSection={<IconChevronDown size={20} stroke={1.5} />}
            leftSection={
              <IconCalendarMinus size={20} stroke={1.5} color="var(--mantine-color-gray-6)" />
            }
            styles={{
              root: {
                borderColor: "var(--mantine-color-cream-4)",
                backgroundColor: "var(--mantine-color-white)",
                color: "var(--mantine-color-burgundy-9)",
                fontWeight: 600,
                fontSize: 14,
                paddingInline: 14,
                paddingBlock: 8,
                height: "auto",
              },
              section: {
                marginInline: 0,
              },
              inner: {
                gap: 10,
              },
            }}
          >
            {triggerLabel}
          </Button>
        </Menu.Target>
        <Menu.Dropdown
          data-testid={
            menuView === "calendar"
              ? "practice-ledger-date-range-custom"
              : "practice-ledger-date-range-menu"
          }
        >
          {menuView === "menu" ? (
            <>
              {NAMED_PRESETS.map((namedPreset) => (
                <Menu.Item
                  py="md"
                  px="lg"
                  fw={600}
                  key={namedPreset}
                  onClick={() => handleSelectNamedPreset(namedPreset)}
                  data-testid={PRACTICE_LEDGER_NAMED_PRESET_TEST_IDS[namedPreset]}
                >
                  {PRACTICE_LEDGER_DATE_RANGE_PRESET_LABEL[namedPreset]}
                </Menu.Item>
              ))}
              <Menu.Item
                py="md"
                px="lg"
                fw={600}
                onClick={handleSelectCustomRange}
                data-testid="practice-ledger-date-range-custom-option"
              >
                {PRACTICE_LEDGER_DATE_RANGE_PRESET_LABEL[PRACTICE_LEDGER_DATE_RANGE_PRESET.Custom]}
              </Menu.Item>
            </>
          ) : (
            <Box p="lg">
              <Stack gap="lg">
                <DatePicker
                  type="range"
                  allowSingleDateInRange
                  value={draftCustomRange}
                  onChange={setDraftCustomRange}
                  data-testid="practice-ledger-date-range-picker"
                />
                <Group gap="lg" wrap="nowrap">
                  <Button
                    color="burgundy"
                    variant="outline"
                    onClick={handleCancelCustomRange}
                    data-testid="practice-ledger-date-range-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    w={179}
                    color="burgundy"
                    disabled={!canApplyCustomRange}
                    onClick={handleApplyCustomRange}
                    data-testid="practice-ledger-date-range-apply"
                  >
                    Apply
                  </Button>
                </Group>
              </Stack>
            </Box>
          )}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
